import { ElementRef } from '@angular/core';
import * as d3 from 'd3';
import { HorizontalGroupBarGraphConfig } from './bar-graph/bar-graph.model';

export abstract class InitializeBarChartService {
  element: ElementRef;
  barGraphConfig: HorizontalGroupBarGraphConfig;
  svg;

  constructor(
    elementParam: ElementRef,
    barGraphConfigParam: HorizontalGroupBarGraphConfig,
  ) {
    this.element = elementParam;
    this.barGraphConfig = barGraphConfigParam;
  }

  get contentWidth(): number {
    const element = this.element.nativeElement;

    return (
      element?.offsetWidth -
      this.barGraphConfig.margin.left -
      this.barGraphConfig.margin.right
    );
  }

  get contentHeight(): number {
    const element = this.element.nativeElement;

    return (
      element?.offsetHeight -
      this.barGraphConfig.margin.top -
      this.barGraphConfig.margin.bottom
    );
  }

  createSvg(): void {
    this.removeSvg();
    const element = this.element.nativeElement;

    this.svg = d3
      .select(element)
      .append('svg')
      .attr('width', element.offsetWidth)
      .attr('height', element.offsetHeight)
      .append('g')
      .attr(
        'transform',
        `translate(${this.barGraphConfig.margin.left}, ${this.barGraphConfig.margin.top})`,
      );
  }

  createAxes(
    xScale: d3.ScaleLinear<number, number, never>,
    yScale: d3.ScaleBand<string>,
  ): void {
    this.svg
      .append('g')
      .attr('class', 'Axis Axis--x')
      .attr('transform', `translate(0,${this.contentHeight})`)
      .call(
        d3
          .axisBottom(xScale)
          .tickSize(0)
          .tickPadding(this.barGraphConfig.tickPadding)
          .tickFormat(
            (d, i) =>
              `${
                this.barGraphConfig.tickFormat && i > 0
                  ? this.barGraphConfig.tickFormat
                  : ''
              }${d}`,
          ),
      );

    this.svg
      .append('g')
      .attr(
        'transform',
        `translate(0,${
          this.barGraphConfig.groupLabelTop
            ? this.barGraphConfig.groupLabelTop - yScale.bandwidth()
            : 0
        })`,
      )
      .attr('class', 'Axis Axis--y')
      .call(
        d3
          .axisLeft(yScale)
          .tickSize(0)
          .tickPadding(this.barGraphConfig.tickPadding),
      );
  }

  createGridLines(xScale: d3.ScaleLinear<number, number, never>): void {
    this.svg
      .append('g')
      .attr('class', 'Grid')
      .attr('transform', `translate(0, ${this.contentHeight})`)
      .call(
        d3
          .axisBottom(xScale)
          .tickSize(-this.contentHeight)
          .tickSizeOuter(0)
          .tickPadding(this.barGraphConfig.tickPadding)
          .tickFormat(
            (d, i) =>
              `${
                this.barGraphConfig.tickFormat && i > 0
                  ? this.barGraphConfig.tickFormat
                  : ''
              }${d}`,
          ),
      );
  }

  createLegends(data: string[]): void {
    const legendsPerRow = this.barGraphConfig.legendsPerRow
      ? this.barGraphConfig.legendsPerRow
      : this.barGraphConfig.legendsPerRow;
    const n = data.length / legendsPerRow;
    const itemWidth = this.barGraphConfig.legendWidth;
    const itemHeight = this.barGraphConfig.legendHeight;
    const colorScale = d3
      .scaleOrdinal()
      .domain(data)
      .range(this.barGraphConfig.colorScheme);

    const legendGroup = this.svg
      .append('g')
      .attr(
        'transform',
        `translate(${-this.barGraphConfig.margin.left},${
          this.contentHeight + this.barGraphConfig.legendPadding
        })`,
      );

    const legend = legendGroup
      .selectAll('.legend')
      .data(data)
      .enter()
      .append('g')
      .attr(
        'transform',
        (_, i) =>
          `translate(${(i % n) * itemWidth},${Math.floor(i / n) * itemHeight})`,
      )
      .attr('class', 'legend');

    legend
      .append('circle')
      .attr('cx', this.barGraphConfig.legendCircleRadius)
      .attr('cy', -this.barGraphConfig.legendCircleRadius)
      .attr('r', this.barGraphConfig.legendCircleRadius)
      .attr('fill', (d) => colorScale(d));

    legend
      .append('text')
      .attr('class', 'Legendlabel')
      .attr(
        'x',
        this.barGraphConfig.legendCircleDiameter +
          this.barGraphConfig.labelLeftMargin,
      )
      .attr('y', 0)
      .text((d) => d);
  }

  getXAxis(): d3.ScaleLinear<number, number, never> {
    return d3.scaleLinear().rangeRound([0, this.contentWidth]);
  }

  getYAxis(padding: number): d3.ScaleBand<string> {
    return d3.scaleBand().rangeRound([0, this.contentHeight]).padding(padding);
  }

  getGroupYAxis(padding: number, bandwidth: number): d3.ScaleBand<string> {
    return d3.scaleBand().padding(padding).rangeRound([0, bandwidth]);
  }

  removeSvg(): void {
    d3.selectAll('.Desktop__Graph svg').remove();
  }
  abstract createBarGraph(): void;
  abstract setScales(): void;
  abstract createGraphBars(): void;
}
